import React, { useContext, useState, useEffect, useRef } from "react";
import update from "immutability-helper";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableBodyRow from "../../components/DragAndDrop";
import CustomTable from "../../components/CustomTable";
import ContentAdd from "./ContentAdd";
import ContentUpdate from "./ContentUpdate";
import useApi from "../../hooks/useApi";
import { deleteContent, ListContent, updateContent } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import usePermissions from "../../hooks/usePermissions";
import { permissionKeys } from "../../constants/staffRole";
import { AppStateContext } from "../../context/AppContext";
import moment from "moment";
import { Button, message, Space, Switch } from "antd";
import { EditOutlined } from "@ant-design/icons";
import DeleteRecord from "../../components/DeleteRecord";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import useDebounce from "../../hooks/useDebounce";

const Content = () => {
  const { isAdd, isDelete, isEdit, isView } = usePermissions({
    key: permissionKeys.Students,
    redirectPermissions: ["isView"],
  });

  const { setPageHeading } = useContext(AppStateContext);
  useEffect(() => {
    setPageHeading("Content Management");
  }, [setPageHeading]);

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(Date.now());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const { request } = useRequest();
  const [filters, setFilters] = useFilter({
    sortField: "order",
    sort: "asc",
    startDate: null,
    endDate: null,
  });

  const openAddModal = () => setAddModal(true);

  useApi({
    callback: ListContent,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => setLoading(false),
  });

  const handleStatusChange = async (checked, belts) => {
    setLoading(true);
    try {
      await updateContent({ isActive: checked }, belts._id);
      message.success(
        `Content ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
  title: "Belt",
  dataIndex: "beltId",
  key: "beltId",
  render: (belt) => belt?.name || "-",
},

    {
      title: "Type",
      dataIndex: "contentType",
      key: "contentType",
      render: (val) => (val === "video" ? "Video" : "Informational"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
          disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => {
              setSelectedContentId(record._id);
              setEditModal(true);
            }}
            icon={<EditOutlined />}
            title="Edit"
          />
          <DeleteRecord
            _id={record._id}
            callback={() => deleteContent(record._id, { isDeleted: true })}
            setLoading={setLoading}
            setRefresh={setRefresh}
            trigger={true}
            type="Video Content"
          />
        </Space>
      ),
    },
  ];

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const saveReorder = useDebounce((orderedIds) => {
    try {
      request({
        url: "admin/content/reorder",
        method: "PATCH",
        data: { orderedIds },
        onSuccess: (resData) => {
          ShowToast(resData?.message || "Content order updated successfully", Severty.SUCCESS);
        },
        onError: (err) => {
          ShowToast(err?.response?.data?.message || "Failed to update order", Severty.ERROR);
        }
      });
    } catch (error) {
      ShowToast(error?.message, Severty.ERROR);
    }
  }, 300);

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRow = dataRef.current[dragIndex];
    const newData = update(dataRef.current, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow],
      ],
    });
    dataRef.current = newData;
    setData(newData);
  };

  const handleDropRow = () => {
    const currentList = dataRef.current || [];
    const orderedIds = currentList.map((item) => item._id);
    saveReorder(orderedIds);
  };

  return (
    <div>
      <DndProvider backend={HTML5Backend}>
        <CustomTable
          addFn={isAdd ? openAddModal : null}
          data={data}
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          columns={columns}
          totalDocs={totalDocs}
          searchField={["title"]}
          searchPlaceholder="Search by title"
          components={{
            body: {
              row: (props) => <DraggableBodyRow {...props} moveRow={moveRow} onDropRow={handleDropRow} />,
            },
          }}
          tableProps={{
            onRow: (record, index) => ({
              index,
              moveRow,
              onDropRow: handleDropRow,
            }),
          }}
        />
      </DndProvider>


      {addModal && (
        <ContentAdd
          visible={addModal}
          setVisible={setAddModal}
          refresh={setRefresh}
        />
      )}
      {editModal && selectedContentId && (
        <ContentUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedContentId={selectedContentId}
          refresh={setRefresh}
        />
      )}
    </div>
  );
};

export default Content;
